/**
 * AttachmentsPreview — Roomgrid attachment rows.
 *
 * Renders only when something is attached (attaching happens from the
 * CONTENT editor toolbar): sunken rows (bg-2, radius 9) with a clip/image
 * icon, mono file name, mono muted size, teal `cid:` chip for inline
 * images and a quiet remove button.
 */

import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import type { NotificationTestFormValues } from '../../types/notificationTest';

const estimateBytes = (base64: string): number => {
  if (!base64) return 0;
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const stripInlineImage = (html: string, contentId: string): string => {
  if (!html || !contentId) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('img[data-cid]').forEach((img) => {
    if (img.getAttribute('data-cid') === contentId) img.remove();
  });
  return doc.body.innerHTML;
};

export const AttachmentsPreview: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const rg = theme.custom.rg;
  const { control, getValues, setValue } =
    useFormContext<NotificationTestFormValues>();

  const attachments = useWatch({ control, name: 'attachments' }) ?? [];

  if (attachments.length === 0) return null;

  const handleRemove = (index: number): void => {
    const att = attachments[index];
    const next = attachments.filter((_, i) => i !== index);
    setValue('attachments', next, { shouldDirty: true, shouldValidate: true });

    if (att?.inline === true && att.contentId) {
      const currentHtml = getValues('message.content') ?? '';
      const updated = stripInlineImage(currentHtml, att.contentId);
      if (updated !== currentHtml) {
        setValue('message.content', updated, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          display: 'block',
          mb: '8px',
          fontSize: '10.5px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          color: rg.text3,
        }}
      >
        {translate('resources.test.attachments.preview.heading', {
          _: 'Attachments',
          smart_count: attachments.length,
        })}
      </Typography>

      <Stack spacing={1}>
        {attachments.map((att, i) => {
          const isImage = (att.type ?? '').startsWith('image/');
          const Icon = isImage ? ImageIcon : AttachFileIcon;
          return (
            <Box
              key={`${att.name}-${i}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                px: '12px',
                py: '9px',
                border: `1px solid ${rg.border}`,
                borderRadius: '9px',
                backgroundColor: rg.bg2,
              }}
            >
              <Icon sx={{ fontSize: 15, color: rg.text3, flex: 'none' }} />
              <Typography
                component="span"
                className="rg-mono"
                sx={{
                  fontSize: '11.5px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {att.name}
              </Typography>
              <Typography
                component="span"
                className="rg-mono"
                sx={{ fontSize: '10.5px', color: rg.text3, flex: 'none' }}
              >
                {formatSize(estimateBytes(att.base64))}
              </Typography>
              {att.inline === true && att.contentId && (
                <Box
                  component="span"
                  className="rg-mono"
                  sx={{
                    fontSize: '10px',
                    fontWeight: 600,
                    px: '7px',
                    py: '1px',
                    borderRadius: '99px',
                    backgroundColor: rg.accentWeak,
                    color: rg.accent,
                    flex: 'none',
                  }}
                >
                  cid:{att.contentId}
                </Box>
              )}
              <IconButton
                onClick={() => handleRemove(i)}
                aria-label="remove attachment"
                sx={{
                  ml: 'auto',
                  width: 24,
                  height: 24,
                  border: 'none',
                  color: rg.text3,
                  '&:hover': { backgroundColor: rg.bgElev },
                  '& .MuiSvgIcon-root': { fontSize: 13 },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          );
        })}

      </Stack>
    </Box>
  );
};

export default AttachmentsPreview;
